// Shared draft-save hook — both `DraftEditor`'s explicit Save button
// and `DraftDrawer`'s dirty-close modal route through this single
// helper so the two surfaces never drift on which API call to make,
// how to apply the response, or which query keys to invalidate.
//
// @aiPurpose Run the same save round-trip from any caller (Save
//   button, dirty-close modal, save-then-send chain).
// @aiInvariant (#1594) The `'saving'` lifecycle phase is begun/settled
//   inside the helper so a caller that forgets to gate is still
//   race-safe — `beginDraftActivity('begin-save')` is rejected (returns
//   `false`) when the machine is not `'idle'`, so the second concurrent
//   invocation early-returns with `{ ok: false }` before any network I/O
//   (exactly one `POST /requests/draft` per double-save).

import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useRequestsStore } from './store.js';
import type { DraftScratchpad } from './types.js';
import { createDraftRequest, updateDraftRequest } from '../../api/requests/index.js';
import { errorMessage } from '../../api/errors.js';
import { invalidateForCreatedRequest, narrowInvalidateForRequest } from './queries.read.js';
import { isSimpleKind } from '@magimp/shared';
import { kindHidesInsurance } from './insurance-visibility.js';
import { buildDraftSaveInput } from './draft-save-input.js';

// WHY (#1599): re-exported so existing call sites and unit tests that
//   import `buildDraftSaveInput` from this module keep working after the
//   transform moved to its own file along the orchestrate/build boundary.
export { buildDraftSaveInput } from './draft-save-input.js';

export type DraftSaveOutcome = { ok: true; persistedId: string } | { ok: false; error: string };

// WHY (#1061): exported sentinel so the Save / save-and-close call
//   sites (DraftEditor onSave, DraftDrawer onConfirmSave) can map an
//   incomplete-insurance refusal to the localised
//   `requests:draft.required.insuranceTriplet` message. Sentinel keeps
//   the i18n boundary at the React layer instead of leaking `t()` into
//   `saveDraftThroughStore` (which runs in a non-React context for
//   the dirty-close + router-blocker save path).
export const DRAFT_SAVE_ERROR_INSURANCE_TRIPLET = '__INSURANCE_TRIPLET_INCOMPLETE__';

export function isInsuranceComplete(d: DraftScratchpad): boolean {
  // WHY: the toggle off branch trivially passes — the save adapter
  //   canonicalises value/currency to null in that case. When the
  //   toggle is on, BOTH cargo fields must be present (the same
  //   both-or-neither shape `enforceInsuranceInvariants` enforces on
  //   create + transition).
  if (d.insuranceRequested !== true) return true;
  if (d.insuranceCargoValue === null || d.insuranceCargoValue === undefined) return false;
  if (d.insuranceCargoCurrency === null || d.insuranceCargoCurrency === undefined) return false;
  return true;
}

// WHY (#1061): exported so the regression test for the
//   "Save drops insurance triplet" bug can assert against the
//   snapshot directly instead of routing through msw + QueryClient.
//   The function is still module-internal in spirit — no production
//   call site reaches for it from outside this file.
export function snapshotDraft(d: DraftScratchpad): DraftScratchpad {
  return {
    manager: d.manager,
    // WHY (#1599): the explicit Save / save-and-close paths route the live
    //   scratchpad through this snapshot before `buildDraftSaveInput`. The
    //   pre-bound funnel link must survive into the snapshot so the FIRST
    //   save still emits it (the adapter gates it behind `includeClientToken`).
    //   Conditional spread keeps the keys absent (not `undefined`) for the
    //   unlinked "+" path under `exactOptionalPropertyTypes`.
    ...(d.leadId !== undefined ? { leadId: d.leadId } : {}),
    ...(d.dealId !== undefined ? { dealId: d.dealId } : {}),
    name: d.name,
    kind: d.kind,
    transportTypeId: d.transportTypeId,
    route: { from: d.route.from, to: d.route.to },
    destinations: d.destinations.map((dest) => ({
      arrivalCityId: dest.arrivalCityId,
      sortOrder: dest.sortOrder,
    })),
    deliveryAddress: d.deliveryAddress,
    // WHY (#1393): mirror `deliveryAddress` — the snapshot is fed into
    //   `buildDraftSaveInput`, which always sends pickupAddress, so the
    //   snapshot must carry it for the round-trip to be faithful.
    pickupAddress: d.pickupAddress,
    // WHY (#1061): the snapshot is fed into `buildDraftSaveInput` on
    //   the explicit Save / save-and-close paths. If we omit the
    //   insurance triplet here, the adapter's `?? false / ?? null`
    //   fallback defaults the request to "not requested" and silently
    //   drops a manager's insurance edits on Save. The triplet round-
    //   trips as-is; the save adapter's own canonicalisation re-asserts
    //   the wire invariant. Conditional spread keeps the legacy-fixture
    //   shape (key omitted vs. `undefined`) under
    //   `exactOptionalPropertyTypes`.
    ...(d.insuranceRequested !== undefined ? { insuranceRequested: d.insuranceRequested } : {}),
    ...(d.insuranceCargoValue !== undefined ? { insuranceCargoValue: d.insuranceCargoValue } : {}),
    ...(d.insuranceCargoCurrency !== undefined
      ? { insuranceCargoCurrency: d.insuranceCargoCurrency }
      : {}),
    // WHY (#1448): the snapshot feeds `buildDraftSaveInput`, which sends the
    //   STANDARD request-level totals. Carry them so the post-save baseline
    //   matches what we pushed and the dirty-diff stays clean after a Save.
    totalWeight: d.totalWeight,
    totalVolume: d.totalVolume,
    items: d.items.map((it) => ({
      ...it,
      photos: [...it.photos],
      packages: it.packages.map((p) => ({ ...p })),
    })),
    boxes: d.boxes.map((b) => ({
      ...b,
      items: b.items.map((it) => ({ ...it, photos: [...it.photos] })),
    })),
    boxesSectionOpen: d.boxesSectionOpen,
    clientToken: d.clientToken,
    status: d.status,
    returnReason: d.returnReason,
  };
}

// WHY (#1471): the Send path saves-then-transitions and records a
//   post-save baseline (`sentSnapshot`) so the dirty-diff collapses to
//   clean. That snapshot's insurance triplet MUST reflect what the save
//   actually PERSISTED, which `buildDraftSaveInput` decides per kind:
//     - SIMPLE / CONTAINER: the save SENDS a forced-off triplet (#1306 /
//       #1393), so the snapshot forces it off too (false / null / null).
//     - STANDARD: the save OMITS the triplet (#1448 preserve-by-omission),
//       so the persisted row keeps any hidden legacy insurance — the
//       snapshot must MIRROR the live scratchpad, NOT force it off. Forcing
//       it off here made a legacy insured STANDARD draft read dirty against
//       an insurance control the redesigned form no longer renders.
//   The STANDARD predicate is computed EXACTLY as `buildDraftSaveInput`
//   (`!isSimpleKind(kind) && kind !== 'CONTAINER'`) so the snapshot and the
//   save payload stay in lockstep. This is NOT `snapshotDraft`: that helper
//   preserves the scratchpad triplet for every kind, which would wrongly
//   keep stale insurance for SIMPLE/CONTAINER (whose save forces it off).
export function buildSentSnapshot(d: DraftScratchpad): DraftScratchpad {
  const isStandard = !isSimpleKind(d.kind) && d.kind !== 'CONTAINER';
  return {
    manager: d.manager,
    // WHY (#1599): the Send path feeds this snapshot into the FIRST save
    //   (`buildDraftSaveInput(sentSnapshot, true)` when `id === null`), so
    //   the pre-bound funnel link must survive here too or a create-then-send
    //   from the funnel would drop it. Conditional spread preserves absence
    //   for the unlinked "+" path under `exactOptionalPropertyTypes`.
    ...(d.leadId !== undefined ? { leadId: d.leadId } : {}),
    ...(d.dealId !== undefined ? { dealId: d.dealId } : {}),
    name: d.name,
    kind: d.kind,
    transportTypeId: d.transportTypeId,
    route: { from: d.route.from, to: d.route.to },
    destinations: d.destinations.map((dest) => ({
      arrivalCityId: dest.arrivalCityId,
      sortOrder: dest.sortOrder,
    })),
    deliveryAddress: d.deliveryAddress,
    pickupAddress: d.pickupAddress,
    insuranceRequested: isStandard ? (d.insuranceRequested ?? false) : false,
    insuranceCargoValue: isStandard ? (d.insuranceCargoValue ?? null) : null,
    insuranceCargoCurrency: isStandard ? (d.insuranceCargoCurrency ?? null) : null,
    roadOptionIds: [...(d.roadOptionIds ?? [])],
    packagingOptionIds: [...(d.packagingOptionIds ?? [])],
    totalWeight: d.totalWeight,
    totalVolume: d.totalVolume,
    items: d.items.map((it) => ({ ...it, photos: [...it.photos] })),
    boxes: d.boxes.map((b) => ({
      ...b,
      items: b.items.map((it) => ({ ...it, photos: [...it.photos] })),
    })),
    boxesSectionOpen: d.boxesSectionOpen,
    clientToken: d.clientToken,
    status: d.status,
    returnReason: d.returnReason,
  };
}

export async function saveDraftThroughStore(qc: QueryClient): Promise<DraftSaveOutcome> {
  const s = useRequestsStore.getState();
  if (s.draft === null) {
    return { ok: false, error: '' };
  }
  // WHY (#1061): refuse Save (including save-and-close) when the
  //   manager has the insurance toggle on but has not filled in both
  //   cargo value and currency. The wire's draft-save schema is
  //   permissive (only rejects `false → non-null`), but persisting a
  //   `true → null/null` draft means a re-opened editor would replay
  //   the same incomplete state and the Send gate would already block
  //   it — better to refuse at write time so the persisted draft
  //   never carries the bad shape.
  // WHY (#1393): skip the insurance-triplet preflight for kinds that hide
  //   the insurance card (SIMPLE and CONTAINER). The save adapter already
  //   forces the triplet off for those kinds in `buildDraftSaveInput`, so
  //   blocking here would leave a CONTAINER draft with stale
  //   `insuranceRequested:true` permanently unsaveable with no visible
  //   control. `kindHidesInsurance` is the single source of truth.
  if (!kindHidesInsurance(s.draft.kind) && !isInsuranceComplete(s.draft)) {
    return { ok: false, error: DRAFT_SAVE_ERROR_INSURANCE_TRIPLET };
  }
  // INVARIANT (#1594): claim the serialized lifecycle machine BEFORE any
  // I/O. A rejected begin (another save/send/close/delete in flight)
  // returns the same `{ ok: false }` busy no-op the old `draftSaving ||
  // draftSending` guard produced, so a double-save fires exactly one
  // `POST /requests/draft`.
  if (!s.beginDraftActivity('begin-save')) {
    return { ok: false, error: '' };
  }
  // INVARIANT (#521 save-race fix): snapshot the scratchpad at the
  // moment we send so the post-success baseline reflects what the
  // server actually persisted. Edits the user types while the save
  // is in-flight remain dirty against this baseline instead of being
  // silently swept up as if they had been saved.
  const sentSnapshot = snapshotDraft(s.draft);
  try {
    const persistedId = s.draftPersistedId;
    const input = buildDraftSaveInput(sentSnapshot, persistedId === null);
    const result =
      persistedId === null
        ? await createDraftRequest(input)
        : await updateDraftRequest(persistedId, input);
    useRequestsStore.getState().applySaveResult({
      persistedId: result.request.id,
      itemIdMap: result.itemIdMap,
      // WHY (#590): optional on the wire; pass through only when the
      //   backend emitted it so the store sees a real `undefined` for
      //   stale-bundle responses (and no remap fires).
      ...(result.packageIdMap !== undefined ? { packageIdMap: result.packageIdMap } : {}),
      // WHY (#599): same pattern for the boxes id maps.
      ...(result.boxIdMap !== undefined ? { boxIdMap: result.boxIdMap } : {}),
      ...(result.boxItemIdMap !== undefined ? { boxItemIdMap: result.boxItemIdMap } : {}),
      sentSnapshot,
    });
    if (persistedId === null) {
      invalidateForCreatedRequest(qc, result.request.id);
    } else {
      narrowInvalidateForRequest(qc, result.request.id);
    }
    return { ok: true, persistedId: result.request.id };
  } catch (err) {
    return { ok: false, error: errorMessage(err) };
  } finally {
    // INVARIANT (#1594): always return the machine to `'idle'` — on
    // success AND failure — so a failed save never leaves a stuck phase.
    useRequestsStore.getState().settleDraftActivity();
  }
}

export function useDraftSave(): () => Promise<DraftSaveOutcome> {
  const qc = useQueryClient();
  return () => saveDraftThroughStore(qc);
}
